import { Observable, Subject } from 'rxjs';
import {
  ResourceByGroup,
  resourceByGroup$,
  ResourceByGroupRef$,
  ResourceByGroupReload$,
  ResourceByGroupSetValue$,
} from './resourceByGroup$';
import { resource$, ResourceLike, ResourceReload$, ResourceSetValue$ } from './resource$';
import { DestroyRef, inject } from '@angular/core';

export type QueryOutputValue<ResourceState, Params, GroupIdentifier> = [unknown] extends [
  GroupIdentifier,
]
  ? ResourceLike<ResourceState, Params>
  : ResourceByGroup<GroupIdentifier, ResourceState, Params>;

export type QueryOutput<ResourceState, Params, GroupIdentifier> = [unknown] extends [
  GroupIdentifier,
]
  ? Observable<ResourceLike<ResourceState, Params>>
  : ResourceByGroupRef$<GroupIdentifier, ResourceState, Params>;

type QueryConfig<ResourceState, Params, GroupIdentifier> =
  | {
      params$: Observable<Params>;
      identifier?: never;
      loader: (param: { params: NonNullable<Params> }) => Observable<ResourceState>;
      setValue$?: (
        resourceSnapshot: () => ResourceLike<NoInfer<ResourceState>, NoInfer<Params>> | undefined,
      ) => Observable<NoInfer<ResourceState> | undefined>;
      initialValue?: NoInfer<ResourceState>;
    }
  | {
      params$: Observable<Params>;
      identifier: (params: NoInfer<NonNullable<Params>>) => GroupIdentifier;
      setValue$?: ResourceByGroupSetValue$<
        NoInfer<GroupIdentifier>,
        NoInfer<ResourceState>,
        NoInfer<Params>
      >;
      loader: (param: { params: NonNullable<Params> }) => Observable<ResourceState>;
      initialValue?: NoInfer<ResourceState>;
    };

export function query<ResourceState, Params, GroupIdentifier>(
  config: QueryConfig<ResourceState, Params, GroupIdentifier>,
  sourcesConfig?: [unknown] extends [GroupIdentifier]
    ? {
        setValue$?: ResourceSetValue$<NoInfer<ResourceState>, NoInfer<Params>>;
        reload$?: ResourceReload$<ResourceState, Params>;
      }
    : {
        setValue$?: ResourceByGroupSetValue$<
          NoInfer<GroupIdentifier>,
          NoInfer<ResourceState>,
          NoInfer<Params>
        >;
        reload$?: ResourceByGroupReload$<GroupIdentifier, ResourceState, Params>;
      },
): QueryOutput<ResourceState, Params, GroupIdentifier> {
  const { params$, loader } = config;
  const hasIdentifier = !!config.identifier;
  const destroy$ = new Subject<void>();

  const _destroyRef = inject(DestroyRef);
  _destroyRef.onDestroy(() => {
    destroy$.next();
    destroy$.complete();
  });
  return (
    hasIdentifier
      ? resourceByGroup$({
          params$,
          identifier: config.identifier as unknown as (
            params: NoInfer<NonNullable<Params>>,
          ) => GroupIdentifier,
          loader: ({ params }) => loader({ params: params as any }),
          initialValue:
            'initialValue' in config ? (config.initialValue as ResourceState) : undefined,
          ...(sourcesConfig?.setValue$
            ? {
                setValue$: sourcesConfig.setValue$ as ResourceByGroupSetValue$<
                  GroupIdentifier,
                  ResourceState,
                  Params
                >,
              }
            : {}),
          ...(sourcesConfig?.reload$
            ? {
                reload$: sourcesConfig.reload$ as ResourceByGroupReload$<
                  GroupIdentifier,
                  ResourceState,
                  Params
                >,
              }
            : {}),
          destroy$,
        })
      : resource$({
          params$,
          loader: ({ params }: { params: Params }) => config.loader({ params: params as any }),
          initialValue:
            'initialValue' in config ? (config.initialValue as ResourceState) : undefined,
          ...(sourcesConfig?.setValue$
            ? {
                setValue$: sourcesConfig.setValue$,
              }
            : {}),
          ...(sourcesConfig?.reload$
            ? {
                reload$: sourcesConfig.reload$ as ResourceReload$<ResourceState, Params>,
              }
            : {}),
          destroy$,
        } as any)
  ) as QueryOutput<ResourceState, Params, GroupIdentifier>;
}
